/**
 * Client for the **phase 1** (system) setup wizard routes. The server
 * gates most of the app with `503 SETUP_REQUIRED` until
 * `POST /setup/finalize` succeeds, so these endpoints sit outside the
 * standard `api` client (which carries auth headers) and are reachable
 * while the rest of the platform is gated.
 *
 * Phase 2 (per-org setup) runs against the normal authed API surface —
 * see `@/lib/org-setup-api`.
 */

const BASE = import.meta.env.VITE_API_BASE_URL || '/api';
const IS_CROSS_ORIGIN = BASE.startsWith('http');

export class SetupApiError extends Error {
  readonly status: number;
  readonly body: Record<string, unknown>;
  constructor(message: string, status: number, body: Record<string, unknown>) {
    super(message);
    this.name = 'SetupApiError';
    this.status = status;
    this.body = body;
  }
}

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const hasBody = init.body != null;
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    // Setup endpoints flip between 200 and 410 across a setup reset, so their
    // responses must never be served from the browser cache — a stale cached
    // 410 otherwise wedges the wizard. The server also sends `no-store`.
    cache: 'no-store',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
    ...(IS_CROSS_ORIGIN && { credentials: 'include' as const }),
  });
  if (!res.ok) {
    let body: Record<string, unknown> = {};
    try {
      const parsed = await res.json();
      if (parsed && typeof parsed === 'object') body = parsed as Record<string, unknown>;
    } catch {
      /* ignore */
    }
    const message =
      (body as { error?: string }).error ?? `Request to ${path} failed with ${res.status}`;
    throw new SetupApiError(message, res.status, body);
  }
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as unknown as T);
}

export interface SetupStateResponse {
  setupRequired: boolean;
  isProduction: boolean;
  hasToken: boolean;
  /**
   * True when the browser still holds a valid `setup_session` cookie — i.e.
   * `POST /setup/verify-token` succeeded earlier and hasn't expired.
   */
  admissionCompleted: boolean;
  /** True when the email-auth plugin is active and the admin step is offered. */
  emailAuthAvailable?: boolean;
  /** True when an email/password admin already exists (skip the admin step). */
  adminAccountExists?: boolean;
}

export interface SetupAuthProvider {
  name: string;
  label: string;
  configured: boolean;
  clientId: string | null;
  hasClientSecret: boolean;
  redirectUri: string | null;
  hasAllowedDomain: boolean;
  allowedDomain: string | null;
  docsUrl?: string | null;
  adminConsoleUrl?: string | null;
  instructions?: string[];
  scopes?: string | null;
}

export interface SetupAuthProviderSecret {
  clientSecret: string | null;
  allowedDomain: string | null;
}

export interface SetupAuthSettings {
  allowedEmailDomains: string[];
  allowedEmails: string[];
  mode: 'off' | 'allow' | 'deny';
  updatedAt: number;
  updatedBy: string | null;
}

export const setupApi = {
  getState: () => fetchJson<SetupStateResponse>('/setup/state'),

  verifyToken: (token: string) =>
    fetchJson<{ ok: true }>('/setup/verify-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  listAuthProviders: () => fetchJson<{ providers: SetupAuthProvider[] }>('/setup/auth-providers'),

  saveAuthProvider: (
    name: string,
    payload: { clientId: string; clientSecret?: string; allowedDomain?: string },
  ) =>
    fetchJson<{ ok: true; redirectUri: string }>(`/setup/auth-providers/${name}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  revealAuthProviderSecret: (name: string) =>
    fetchJson<SetupAuthProviderSecret>(`/setup/auth-providers/${name}/secret`),

  getAuthDomains: () => fetchJson<SetupAuthSettings>('/setup/auth-domains'),

  saveAuthDomains: (
    allowedEmailDomains: string[],
    mode: SetupAuthSettings['mode'] = 'allow',
    allowedEmails: string[] = [],
  ) =>
    fetchJson<SetupAuthSettings>('/setup/auth-domains', {
      method: 'PUT',
      body: JSON.stringify({ allowedEmailDomains, allowedEmails, mode }),
    }),

  /**
   * Create the first email/password administrator. Promotes the account to
   * super-admin and signs the operator in (session cookie). Only succeeds
   * while the setup gate is open and the email-auth plugin is active.
   */
  createAdminAccount: (input: { email: string; password: string; name?: string }) =>
    fetchJson<{ ok: true }>('/setup/admin-account', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  finalize: () =>
    fetchJson<{ ok: true }>('/setup/finalize', {
      method: 'POST',
    }),
};
